import { useEffect, useMemo, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { getDocumentSignedUrl as getDocSignedUrl, parseDocumentPath } from "@/lib/documentDownload";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeDocumentContent } from "@/lib/sanitize";

// Configure pdf.js worker (bundled)
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

type NativePdfPreviewProps = {
  url: string;
  fallbackHtml?: string;
  /** Max render width for the PDF page (will shrink on small screens) */
  maxWidth?: number;
  /** Optional className applied to the outer wrapper */
  className?: string;
  /** Optional className applied to the scroll container (e.g. "h-[75vh]") */
  heightClassName?: string;
};

export function NativePdfPreview({
  url,
  fallbackHtml,
  maxWidth = 900,
  className,
  heightClassName,
}: NativePdfPreviewProps) {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [pdfFailed, setPdfFailed] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setPdfFailed(false);
    setResolvedUrl(null);

    (async () => {
      // If it's already a full http(s) URL that isn't a supabase storage URL, use as-is.
      const path = parseDocumentPath(url);
      if (!path) {
        if (isMounted) setResolvedUrl(url);
        return;
      }
      try {
        const { data } = await supabase.storage
          .from("documents")
          .createSignedUrl(path, 300);
        if (isMounted) setResolvedUrl(data?.signedUrl || url);
      } catch {
        if (isMounted) setResolvedUrl(url);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [url]);

  const pageWidth = useMemo(() => {
    if (typeof window === "undefined") return maxWidth;
    return Math.min(maxWidth, Math.max(320, window.innerWidth - 96));
  }, [maxWidth]);

  return (
    <div className={className}>
      <div className={heightClassName ? `w-full overflow-auto ${heightClassName}` : "w-full overflow-auto"}>
        {pdfFailed && fallbackHtml ? (
          <div
            className="p-6 ecclesiastical-document max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeDocumentContent(fallbackHtml) }}
          />
        ) : (
        <div className="flex justify-center">
          {!resolvedUrl ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading PDF...
            </div>
          ) : (
          <Document
            file={resolvedUrl}
            onLoadSuccess={({ numPages: n }: { numPages: number }) => {
              setNumPages(n);
              setPageNumber(1);
            }}
            onLoadError={() => setPdfFailed(true)}
            loading={
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading PDF...
              </div>
            }
            error={<div className="text-destructive">Failed to load PDF</div>}
          >
            <Page
              pageNumber={pageNumber}
              width={pageWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
          )}
        </div>
        )}
      </div>

      {!pdfFailed && numPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Page <span className="text-foreground">{pageNumber}</span> of{" "}
            <span className="text-foreground">{numPages}</span>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
            disabled={pageNumber >= numPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
